import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AppState } from '@app/reducers';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent, LoadingStateComponent, SbDialogModule, ToastService } from '@sb/ui';

export interface NavatticDemoDialogData {
  demoId: string;
}

@Component({
  selector: 'navattic-demo-dialog',
  standalone: true,
  imports: [SbDialogModule, ButtonComponent, LoadingStateComponent, TranslateModule],
  templateUrl: './navattic-demo-dialog.component.html',
})
export class NavatticDemoDialogComponent implements OnInit {
  public loading: boolean;
  private bailTimeout: ReturnType<typeof setTimeout>;

  @ViewChild('iframeContainer', { static: true })
  public iframeContainer: ElementRef;

  private navatticEventListener: (event: MessageEvent) => void;
  private iframe: HTMLIFrameElement;

  public constructor(
    @Inject(DIALOG_DATA) public readonly data: NavatticDemoDialogData,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly store: Store<AppState>,
    private readonly translate: TranslateService,
    private readonly dialogRef: DialogRef<boolean>,
    private readonly toast: ToastService,
  ) {}

  public ngOnInit(): void {
    this.loading = true;
    this.iframe = this.createDemoIframe(this.data.demoId);
    this.appendDemoIframe(this.iframeContainer.nativeElement).then((opened) => {
      if (opened) {
        this.loading = false;
      } else {
        this.toast.openDanger(this.translate.instant('Something went wrong trying to start the tour'));
        this.close(false);
      }
    });
  }

  public createDemoIframe(demoId: string): HTMLIFrameElement {
    const iframe = this.document.createElement('iframe');

    iframe.src = `https://capture.navattic.com/${demoId}`;
    iframe.style.cssText = 'border:none;width:100%;height:100%;z-index:1';
    iframe.setAttribute('allow', 'fullscreen');
    iframe.setAttribute('data-navattic-demo-id', demoId);

    return iframe;
  }

  public appendDemoIframe(container: HTMLElement): Promise<boolean> {
    return new Promise((resolve) => {
      container.appendChild(this.iframe);

      this.navatticEventListener = (event) => {
        if (event.origin !== 'https://capture.navattic.com') return;

        if (event.data.type === 'START_FLOW') {
          clearTimeout(this.bailTimeout);
          resolve(true);
        }

        if (event.data.type === 'COMPLETE_FLOW') {
          this.close(true);
        }
      };
      window.addEventListener('message', this.navatticEventListener);

      // Ideally we simply fetch the resource and if that would fail (e.g. because of ad blockers), we'd bail.
      // But because of cors issues we sadly can't do that.
      // So instead we listen for a navattic specific event and if that isn't emitted within 10 seconds, we bail.
      this.bailTimeout = setTimeout(() => resolve(false), 10000);
    });
  }

  private cleanUpDemo() {
    window.removeEventListener('message', this.navatticEventListener);
    clearTimeout(this.bailTimeout);
    this.iframe.remove();
  }

  public close(demoCompleted: boolean) {
    this.cleanUpDemo();
    this.dialogRef.close(demoCompleted);
  }
}
